import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import { useLiveFeed, type SpreadData } from "@/hooks/use-live-feed";
import {
  ASYMMETRIC_QUICK_FILL_PRESETS,
  KNOWN_CONTRACT_SIZES,
  SYMMETRIC_QUICK_FILL_PERCENTS,
  fmtPrice,
  normalizeSymbol,
  parsePositive,
  priceMapKey,
  symbolMid,
} from "./bracket-utils";

export type BracketMode = "price" | "distance";
export type BracketPerspective = "buy" | "sell";

export interface BracketState {
  stopLoss: string;
  takeProfit: string;
  mode: BracketMode;
  /** Which leg the operator is *typing from*. The mirror in HedgePair
   *  DO is symmetric, so the resolved distances are the same either
   *  way — perspective only changes which side of mid the picker
   *  validates against and what the quick-fill chips fill in. */
  perspective: BracketPerspective;
}

export type BracketResolved =
  | { ok: true; slDistancePrice: number; tpDistancePrice: number; referencePrice: number | null }
  | { ok: false; error: string };

/** Open position the picker can offer to copy SL/TP from. The caller
 *  passes the raw open positions on the leg accounts; the picker
 *  filters by symbol + non-null brackets. */
export interface RunningPositionRef {
  accountId: string;
  /** Which side of THIS pair's accounts the position sits on. The
   *  picker uses this to set perspective on copy. */
  legSide: BracketPerspective;
  /** Position's actual side ("buy" | "sell") — usually matches
   *  legSide on a clean hedge but doesn't have to. The "Copy" button
   *  always sets perspective = legSide so the inputs land on the
   *  correct side of mid. */
  positionSide: "buy" | "sell";
  ticket: number;
  symbol: string;
  lots: number;
  entry: number;
  stopLoss: number | null;
  takeProfit: number | null;
  /** Pre-resolved firm label, e.g. "FTMO". Used in the button text. */
  legLabel: string;
}

export interface HedgeBracketPickerProps {
  /** Already-uppercased / aliased symbol (caller normalizes before
   *  passing in). The picker re-normalizes anyway as a safety net. */
  symbol: string;
  /** Display label for the buy leg, e.g. "FTMO". */
  buyLegLabel: string;
  /** Display label for the sell leg, e.g. "Maven Markets". */
  sellLegLabel: string;
  /** Current SL/TP/mode/perspective. Picker is fully controlled. */
  value: BracketState;
  /** Setter for the SL/TP/mode/perspective values. */
  onChange(next: BracketState): void;
  /** Fires whenever the resolved bracket changes — caller stores it
   *  and uses it on submit. */
  onResolved(b: BracketResolved): void;
  /** When provided, the picker uses this as the live quote source
   *  instead of subscribing to its own broker-radar feed. Avoids
   *  double-subscriptions when a parent is already streaming. */
  liveQuote?: SpreadData | null;
  /** When true, the picker pre-fills SL/TP at ±0.5% the FIRST time it
   *  sees a reference price (and only if both fields are empty). */
  autoFillOnFirstQuote?: boolean;
  /** Optional pair contract size for the lots/SL → margin hint that
   *  the dialog used to render in its footer. The terminal panel
   *  doesn't surface it, so the prop is optional. */
  contractSize?: number | null;
  /** Open positions on the leg accounts. The picker filters to
   *  same-symbol matches with non-null SL+TP and surfaces a "Copy"
   *  button per match. Empty / missing → no copy UI. */
  runningPositions?: RunningPositionRef[];
}

/**
 * Reusable Stop-Loss / Take-Profit picker for a hedge open.
 *
 * The operator types from one leg's perspective at a time. Picker
 * resolves both SL/TP into ONE pair of positive distances; the
 * HedgePair DO mirrors them around the consensus reference price so
 * the other leg gets the inverse pair automatically.
 *
 * Three operator-facing features:
 *   - **Prominent perspective card** with leg color + switcher chips,
 *     so it's never ambiguous which leg the typed numbers belong to.
 *   - **Perspective swap**: in Price mode, switching from BUY to SELL
 *     mirrors the inputs across mid (and vice versa). The same hedge
 *     is just viewed from the other leg.
 *   - **Copy from open position**: when the leg accounts already have
 *     an open position on the same symbol, one click copies that
 *     position's SL/TP into the picker.
 */
export function HedgeBracketPicker({
  symbol,
  buyLegLabel,
  sellLegLabel,
  value,
  onChange,
  onResolved,
  liveQuote: liveQuoteOverride,
  autoFillOnFirstQuote = false,
  contractSize,
  runningPositions,
}: HedgeBracketPickerProps): ReactNode {
  const normalized = normalizeSymbol(symbol);

  // useLiveFeed is a refcounted singleton — calling it again when a
  // parent already does is free, so we always subscribe and let the
  // override (when provided) take precedence for the active symbol.
  const ownFeed = useLiveFeed("opofinance", "ecn");
  const ownPriceMap = useMemo(() => {
    const map = new Map<string, SpreadData>();
    for (const item of ownFeed.data) map.set(priceMapKey(item), item);
    return map;
  }, [ownFeed.data]);

  const liveQuote: SpreadData | null =
    liveQuoteOverride !== undefined
      ? (liveQuoteOverride ?? null)
      : (ownPriceMap.get(normalized) ?? null);
  const liveQuoteMid = symbolMid(liveQuote);

  // Fallback /api/market/quote poller — same behavior as the dialog's
  // pre-extraction implementation. Only runs while broker-radar is
  // silent for this symbol, so a healthy WS feed pays nothing.
  const [fallbackPrice, setFallbackPrice] = useState<number | null>(null);
  useEffect(() => {
    if (liveQuoteMid != null) {
      setFallbackPrice(null);
      return;
    }
    let cancelled = false;
    const tick = async (): Promise<void> => {
      try {
        const res = await api.get<{ mid: number }>(
          `/api/market/quote?symbol=${encodeURIComponent(normalized)}`,
        );
        if (!cancelled && typeof res.mid === "number") setFallbackPrice(res.mid);
      } catch {
        if (!cancelled) setFallbackPrice(null);
      }
    };
    void tick();
    const iv = setInterval(() => void tick(), 4_000);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, [normalized, liveQuoteMid]);

  const referencePrice = liveQuoteMid ?? fallbackPrice;
  const priceSourceDelayed = liveQuoteMid == null && fallbackPrice != null;

  const isBuyView = value.perspective === "buy";
  const activeLegLabel = isBuyView ? buyLegLabel : sellLegLabel;
  const oppositeLegLabel = isBuyView ? sellLegLabel : buyLegLabel;
  const oppositeSide = isBuyView ? "SELL" : "BUY";

  // Auto-fill ±0.5% the FIRST time we see a reference price, only if
  // both inputs are empty. Tracked per-symbol+perspective so flipping
  // perspective doesn't auto-overwrite operator input on the new view.
  const autoFilledKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!autoFillOnFirstQuote) return;
    if (value.mode !== "price") return;
    if (referencePrice == null) return;
    const key = `${normalized}::${value.perspective}`;
    if (autoFilledKeyRef.current === key) return;
    if (value.stopLoss !== "" || value.takeProfit !== "") {
      autoFilledKeyRef.current = key;
      return;
    }
    autoFilledKeyRef.current = key;
    onChange({
      ...value,
      stopLoss: fmtPrice(referencePrice * (isBuyView ? 0.995 : 1.005)),
      takeProfit: fmtPrice(referencePrice * (isBuyView ? 1.005 : 0.995)),
    });
  }, [autoFillOnFirstQuote, value, normalized, referencePrice, isBuyView, onChange]);

  const parsedStop = parsePositive(value.stopLoss);
  const parsedTake = parsePositive(value.takeProfit);

  const bracket: BracketResolved = useMemo(() => {
    if (!parsedStop || !parsedTake) {
      return { ok: false, error: "Stop loss and take profit must be greater than zero." };
    }
    if (value.mode === "distance") {
      return {
        ok: true,
        slDistancePrice: parsedStop,
        tpDistancePrice: parsedTake,
        referencePrice,
      };
    }
    if (referencePrice == null) {
      return { ok: false, error: "Live market price is unavailable for this symbol right now." };
    }
    if (isBuyView) {
      // BUY view: SL must be BELOW mid, TP ABOVE.
      if (!(parsedStop < referencePrice && parsedTake > referencePrice)) {
        return {
          ok: false,
          error: "Stop loss must be below the current market and take profit above it.",
        };
      }
      return {
        ok: true,
        slDistancePrice: referencePrice - parsedStop,
        tpDistancePrice: parsedTake - referencePrice,
        referencePrice,
      };
    }
    // SELL view: SL must be ABOVE mid, TP BELOW.
    if (!(parsedStop > referencePrice && parsedTake < referencePrice)) {
      return {
        ok: false,
        error: "Stop loss must be above the current market and take profit below it (selling).",
      };
    }
    return {
      ok: true,
      slDistancePrice: parsedStop - referencePrice,
      tpDistancePrice: referencePrice - parsedTake,
      referencePrice,
    };
  }, [value.mode, parsedStop, parsedTake, referencePrice, isBuyView]);

  // Notify parent when the resolved value changes. Caller relies on
  // this for the submit button's disabled state.
  useEffect(() => {
    onResolved(bracket);
  }, [bracket, onResolved]);

  const setMode = useCallback(
    (next: BracketMode) => onChange({ ...value, mode: next }),
    [onChange, value],
  );
  const setStopLoss = useCallback(
    (next: string) => onChange({ ...value, stopLoss: next }),
    [onChange, value],
  );
  const setTakeProfit = useCallback(
    (next: string) => onChange({ ...value, takeProfit: next }),
    [onChange, value],
  );

  // Switch perspective. In Price mode with a reference price, mirror
  // both inputs across mid so the same hedge is shown from the other
  // leg (BUY's SL price = SELL's TP price = same mirrored point).
  const setPerspective = useCallback(
    (next: BracketPerspective) => {
      if (next === value.perspective) return;
      if (value.mode === "price" && referencePrice != null) {
        const mirror = (s: string): string => {
          const n = parsePositive(s);
          if (n == null) return s;
          return fmtPrice(2 * referencePrice - n);
        };
        onChange({
          ...value,
          perspective: next,
          stopLoss: mirror(value.stopLoss),
          takeProfit: mirror(value.takeProfit),
        });
        return;
      }
      onChange({ ...value, perspective: next });
    },
    [onChange, value, referencePrice],
  );

  // Custom % input — operator types e.g. 0.37 and presses Enter; SL
  // and TP are filled symmetrically around the live mid for the
  // active perspective.
  const [customPctInput, setCustomPctInput] = useState("");
  const applyCustomPct = (): void => {
    const pct = Number(customPctInput);
    if (!Number.isFinite(pct) || pct <= 0 || referencePrice == null) return;
    onChange({
      ...value,
      stopLoss: fmtPrice(referencePrice * (isBuyView ? 1 - pct / 100 : 1 + pct / 100)),
      takeProfit: fmtPrice(referencePrice * (isBuyView ? 1 + pct / 100 : 1 - pct / 100)),
    });
  };

  const effectiveContractSize =
    contractSize != null && contractSize > 0
      ? contractSize
      : (KNOWN_CONTRACT_SIZES[normalized] ?? null);

  // Copy-from-position candidates: any same-leg open position with a
  // full bracket. Same-symbol matches sort first; cross-symbol matches
  // (e.g. operator is staging a EURUSD hedge but already has an open
  // XAUUSD on the same leg account) still surface so the operator can
  // mirror an existing bracket without re-typing it. The chip subtitle
  // labels the source symbol so a cross-symbol copy is always
  // disambiguated.
  const copyCandidates = useMemo(() => {
    if (!runningPositions || runningPositions.length === 0) return [];
    const filtered = runningPositions.filter(
      (p) => p.stopLoss != null && p.takeProfit != null,
    );
    return filtered.sort((a, b) => {
      const aMatch = a.symbol.trim().toUpperCase() === normalized ? 0 : 1;
      const bMatch = b.symbol.trim().toUpperCase() === normalized ? 0 : 1;
      if (aMatch !== bMatch) return aMatch - bMatch;
      return a.ticket - b.ticket;
    });
  }, [runningPositions, normalized]);

  const copyFromPosition = (pos: RunningPositionRef): void => {
    if (pos.stopLoss == null || pos.takeProfit == null) return;
    onChange({
      mode: "price",
      perspective: pos.legSide,
      stopLoss: fmtPrice(pos.stopLoss),
      takeProfit: fmtPrice(pos.takeProfit),
    });
  };

  // Visual treatment for the perspective indicator. Emerald for BUY,
  // rose for SELL. Defined as className strings so Tailwind picks up
  // the actual classes at build time (no dynamic interpolation).
  const perspectiveCardClasses = isBuyView
    ? "border-emerald-500/40 bg-emerald-500/5"
    : "border-rose-500/40 bg-rose-500/5";
  const perspectiveTitleClasses = isBuyView
    ? "text-emerald-700 dark:text-emerald-400"
    : "text-rose-700 dark:text-rose-400";

  return (
    <div className="grid gap-4">
      {/* Bid/ask strip. */}
      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.18, ease: "easeOut" }}
        className="rounded-lg border border-border/60 bg-muted/30 p-3"
      >
        <div className="flex items-center justify-between gap-2 text-xs">
          <div className="font-medium uppercase tracking-wide text-muted-foreground">
            {normalized}
          </div>
          <div className="text-muted-foreground">
            {liveQuote != null && liveQuote.isOpen ? (
              <span className="text-emerald-600">● live</span>
            ) : priceSourceDelayed ? (
              <span className="text-amber-600">fallback feed · may lag</span>
            ) : (
              <span className="text-destructive">no quote</span>
            )}
          </div>
        </div>
        <div className="mt-2 grid grid-cols-2 gap-2">
          <div className="rounded-md border border-rose-500/30 bg-rose-500/5 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wide text-rose-600">Sell · Bid</div>
            <div className="font-mono text-xl font-semibold tabular-nums text-rose-600">
              {liveQuote != null ? fmtPrice(liveQuote.bid) : "—"}
            </div>
          </div>
          <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wide text-emerald-600">Buy · Ask</div>
            <div className="font-mono text-xl font-semibold tabular-nums text-emerald-600">
              {liveQuote != null ? fmtPrice(liveQuote.ask) : "—"}
            </div>
          </div>
        </div>
        <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground tabular-nums">
          <div>
            Mid:{" "}
            <span className="font-mono text-foreground">
              {referencePrice != null ? fmtPrice(referencePrice) : "—"}
            </span>
          </div>
          {liveQuote != null ? (
            <div>
              Spread:{" "}
              <span className="font-mono text-foreground">
                {fmtPrice(liveQuote.ask - liveQuote.bid)}
              </span>
            </div>
          ) : null}
        </div>
      </motion.div>

      {/* Mode toggle. */}
      <div className="grid gap-2">
        <label className="text-xs font-medium text-muted-foreground">Bracket Mode</label>
        <div className="flex flex-wrap gap-2">
          {([
            ["price", "Price"],
            ["distance", "Distance"],
          ] as const).map(([modeValue, label]) => (
            <Button
              key={modeValue}
              type="button"
              size="sm"
              variant={value.mode === modeValue ? "default" : "outline"}
              onClick={() => setMode(modeValue)}
            >
              {label}
            </Button>
          ))}
        </div>
      </div>

      {/* Perspective indicator card — high-contrast box right where
          the operator looks for context. Same place every time so it
          can't be missed. */}
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={value.perspective}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.14 }}
          className={cn("rounded-lg border-2 p-3", perspectiveCardClasses)}
        >
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Setting SL/TP From
              </div>
              <div className={cn("mt-0.5 text-base font-semibold", perspectiveTitleClasses)}>
                The {isBuyView ? "BUY" : "SELL"} Leg
                <span className="ml-2 text-sm text-muted-foreground">
                  · {activeLegLabel}
                </span>
              </div>
              <div className="mt-1 text-[11px] text-muted-foreground">
                The {oppositeSide} leg ({oppositeLegLabel}) gets the mirrored pair automatically —
                its SL price = your TP, its TP price = your SL.
              </div>
            </div>
            <div className="grid grid-cols-2 gap-1 sm:grid-cols-1">
              <Button
                type="button"
                size="sm"
                variant={isBuyView ? "default" : "outline"}
                onClick={() => setPerspective("buy")}
                className={cn(
                  "h-7 text-xs",
                  isBuyView && "bg-emerald-500 text-white hover:bg-emerald-500/90",
                )}
              >
                Buy View
              </Button>
              <Button
                type="button"
                size="sm"
                variant={!isBuyView ? "default" : "outline"}
                onClick={() => setPerspective("sell")}
                className={cn(
                  "h-7 text-xs",
                  !isBuyView && "bg-rose-500 text-white hover:bg-rose-500/90",
                )}
              >
                Sell View
              </Button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Copy from running position — only shown when there's at
          least one match. */}
      {copyCandidates.length > 0 && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3">
          <div className="text-[10px] uppercase tracking-wider text-amber-700 dark:text-amber-400">
            Copy SL/TP From An Open Position
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {copyCandidates.map((pos) => {
              const slStr = pos.stopLoss != null ? fmtPrice(pos.stopLoss) : "—";
              const tpStr = pos.takeProfit != null ? fmtPrice(pos.takeProfit) : "—";
              const sideTag = pos.positionSide.toUpperCase();
              const posSymbol = pos.symbol.trim().toUpperCase();
              const isCrossSymbol = posSymbol !== normalized;
              return (
                <Button
                  key={`${pos.accountId}-${pos.ticket}`}
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => copyFromPosition(pos)}
                  className="h-auto py-1.5 text-left"
                  title={
                    isCrossSymbol
                      ? `Copy SL ${slStr} / TP ${tpStr} from ${posSymbol} #${pos.ticket} (different symbol)`
                      : `Copy SL ${slStr} / TP ${tpStr} from #${pos.ticket}`
                  }
                >
                  <div className="flex flex-col items-start gap-0.5 leading-tight">
                    <span className="font-mono text-[10px] font-semibold tabular-nums">
                      {sideTag} #{pos.ticket} · {pos.lots.toFixed(2)} lots
                      {isCrossSymbol && (
                        <span className="ml-1 rounded-sm bg-amber-500/20 px-1 text-amber-700 dark:text-amber-400">
                          {posSymbol}
                        </span>
                      )}
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      {pos.legLabel} · SL {slStr} / TP {tpStr}
                    </span>
                  </div>
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {/* SL / TP inputs. */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="grid gap-2">
          <label className="text-xs font-medium text-muted-foreground">
            {value.mode === "price" ? "Stop Loss Price" : "Stop Loss Distance"}
          </label>
          <Input
            value={value.stopLoss}
            onChange={(e) => setStopLoss(e.target.value)}
            inputMode="decimal"
          />
          {value.mode === "price" && referencePrice != null && parsedStop != null ? (
            <div className="text-xs tabular-nums text-rose-600">
              {(() => {
                // For BUY view, SL should be below mid (negative diff).
                // For SELL view, SL should be above mid (positive diff).
                const diff = isBuyView
                  ? referencePrice - parsedStop
                  : parsedStop - referencePrice;
                const pct = (Math.abs(diff) / referencePrice) * 100;
                if (diff > 0) {
                  const sign = isBuyView ? "−" : "+";
                  return `${sign} ${fmtPrice(Math.abs(diff))} · ${sign}${pct.toFixed(2)}% from mid`;
                }
                return isBuyView ? "must be below current mid" : "must be above current mid";
              })()}
            </div>
          ) : null}
        </div>
        <div className="grid gap-2">
          <label className="text-xs font-medium text-muted-foreground">
            {value.mode === "price" ? "Take Profit Price" : "Take Profit Distance"}
          </label>
          <Input
            value={value.takeProfit}
            onChange={(e) => setTakeProfit(e.target.value)}
            inputMode="decimal"
          />
          {value.mode === "price" && referencePrice != null && parsedTake != null ? (
            <div className="text-xs tabular-nums text-emerald-600">
              {(() => {
                const diff = isBuyView
                  ? parsedTake - referencePrice
                  : referencePrice - parsedTake;
                const pct = (Math.abs(diff) / referencePrice) * 100;
                if (diff > 0) {
                  const sign = isBuyView ? "+" : "−";
                  return `${sign} ${fmtPrice(Math.abs(diff))} · ${sign}${pct.toFixed(2)}% from mid`;
                }
                return isBuyView ? "must be above current mid" : "must be below current mid";
              })()}
            </div>
          ) : null}
        </div>
      </div>

      {/* Quick-fill chips. */}
      <AnimatePresence initial={false}>
        {value.mode === "price" && referencePrice != null ? (
          <motion.div
            key="quick-fill"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="grid gap-2 overflow-hidden"
          >
            <div className="grid gap-2">
              <label className="text-xs font-medium text-muted-foreground">
                Quick Fill — symmetric SL/TP around current mid
              </label>
              <div className="flex flex-wrap gap-2">
                {SYMMETRIC_QUICK_FILL_PERCENTS.map((pct) => {
                  const sl = fmtPrice(
                    referencePrice * (isBuyView ? 1 - pct / 100 : 1 + pct / 100),
                  );
                  const tp = fmtPrice(
                    referencePrice * (isBuyView ? 1 + pct / 100 : 1 - pct / 100),
                  );
                  return (
                    <Button
                      key={pct}
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => onChange({ ...value, stopLoss: sl, takeProfit: tp })}
                      title={`SL ${sl} · TP ${tp}`}
                    >
                      ±{pct}%
                    </Button>
                  );
                })}
              </div>
            </div>

            <div className="grid gap-2">
              <label className="text-xs font-medium text-muted-foreground">
                Asymmetric Presets — wider TP than SL (good for news fades)
              </label>
              <div className="flex flex-wrap gap-2">
                {ASYMMETRIC_QUICK_FILL_PRESETS.map(([slPct, tpPct]) => {
                  const sl = fmtPrice(
                    referencePrice * (isBuyView ? 1 - slPct / 100 : 1 + slPct / 100),
                  );
                  const tp = fmtPrice(
                    referencePrice * (isBuyView ? 1 + tpPct / 100 : 1 - tpPct / 100),
                  );
                  return (
                    <Button
                      key={`${slPct}-${tpPct}`}
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => onChange({ ...value, stopLoss: sl, takeProfit: tp })}
                      title={`SL ${sl} · TP ${tp}`}
                    >
                      SL {slPct}% / TP {tpPct}%
                    </Button>
                  );
                })}
              </div>
            </div>

            <div className="grid gap-2">
              <label className="text-xs font-medium text-muted-foreground">
                Custom % — symmetric SL/TP at any precision
              </label>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">±</span>
                <Input
                  value={customPctInput}
                  onChange={(e) => setCustomPctInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      applyCustomPct();
                    }
                  }}
                  inputMode="decimal"
                  placeholder="0.37"
                  className="h-8 max-w-[100px] font-mono text-sm tabular-nums"
                />
                <span className="text-xs text-muted-foreground">%</span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={applyCustomPct}
                  disabled={
                    !Number.isFinite(Number(customPctInput)) ||
                    Number(customPctInput) <= 0 ||
                    referencePrice == null
                  }
                >
                  Apply
                </Button>
              </div>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* Resolution footer. */}
      <div className="rounded-lg border border-border/60 bg-muted/20 p-3 text-xs text-muted-foreground">
        <div>
          Buy opens on <span className="font-medium text-foreground">{buyLegLabel}</span>. Sell opens on{" "}
          <span className="font-medium text-foreground">{sellLegLabel}</span>.
        </div>
        {effectiveContractSize != null ? (
          <div className="mt-2">
            Contract size{" "}
            <span className="font-medium text-foreground">
              {effectiveContractSize.toLocaleString()}
              {contractSize == null ? " (default)" : ""}
            </span>
            .
          </div>
        ) : null}
        {bracket.ok ? (
          <div className="mt-2">
            Mirrored distances: SL {fmtPrice(bracket.slDistancePrice)}
            {" · "}TP {fmtPrice(bracket.tpDistancePrice)}
          </div>
        ) : (
          <div className="mt-2 text-destructive">{bracket.error}</div>
        )}
      </div>
    </div>
  );
}
